import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AdminSearchUsers, AdminRegisterUser, CreateRedsysPayment } from '../../Backend/ApiCalls';
import { savePendingRedsysRedirect } from './PagoEncuestaRedirectComponent';
import { formatCents } from '../../utils/formatMoney';
import { hasPaymentConfiguration } from '../../utils/paymentConfiguration';
import './AdminAddParticipantModal.css';

const ageLabel = (ageType) =>
  Number(ageType) === 0 ? 'Exento'
    : Number(ageType) === 1 ? 'Infantil'
      : Number(ageType) === 2 ? 'Juvenil'
        : Number(ageType) === 3 ? 'Adulto'
          : 'Desconocido';

const optionPrice = (option, ageType) => {
  const prices = option?.pricesByAgeType || option?.text?.pricesByAgeType || {};
  const price = Number(prices[String(ageType)] ?? 0);
  return Number.isFinite(price) && price > 0 ? price : 0;
};

const optionText = (option) =>
  typeof option?.text === 'string' ? option.text : (option?.text?.text ?? String(option?.id ?? ''));

/**
 * Modal administrativo para inscribir/responder una encuesta en nombre de
 * otra persona (Parte 2 de la mejora de inscripción administrativa).
 * Nunca pide contraseña ni token de la persona: todo va con la sesión del
 * propio administrador, que el backend valida contra isPollManager.
 *
 * Pasos: buscar -> responder (si la encuesta tiene preguntas) -> forma de
 * pago (si la encuesta tiene pago activado) -> resumen -> confirmar.
 */
export const AdminAddParticipantModal = ({ poll, onClose, onSuccess, showNoti }) => {
  const [step, setStep] = useState('search'); // search | answer | payment | summary
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchedOnce, setSearchedOnce] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null); // { id, name, surname, ageType, isRegistered, paymentStatus }
  const [answers, setAnswers] = useState({}); // questionIndex -> value
  const [paymentChoice, setPaymentChoice] = useState('none'); // none | manual | online
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceRef = useRef(null);

  const hasQuestions = Array.isArray(poll?.questions) && poll.questions.length > 0;
  const paymentEnabled = hasPaymentConfiguration(poll);

  // Búsqueda con debounce (Parte 3): no consulta en cada pulsación.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length < 2) {
      setResults([]);
      setSearchedOnce(false);
      return undefined;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { response, data } = await AdminSearchUsers(poll.id, query.trim());
        if (response.ok) {
          setResults(Array.isArray(data?.results) ? data.results : []);
        } else {
          setResults([]);
        }
      } catch (error) {
        console.error('AdminSearchUsers error:', error);
        setResults([]);
      } finally {
        setIsSearching(false);
        setSearchedOnce(true);
      }
    }, 350);

    return () => clearTimeout(debounceRef.current);
  }, [query, poll.id]);

  const handleSelectPerson = (person) => {
    setSelectedPerson(person);
    setAnswers({});
    setPaymentChoice('none');
    setStep(hasQuestions ? 'answer' : (paymentEnabled ? 'payment' : 'summary'));
  };

  const handleAnswerChange = (qIndex, question, value) => {
    setAnswers((prev) => {
      const next = { ...prev };
      if (question.type === 'multiChoice') {
        const current = Array.isArray(prev[qIndex]) ? prev[qIndex] : [];
        next[qIndex] = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];
      } else if (question.type === 'singleChoice') {
        next[qIndex] = value;
      } else {
        next[qIndex] = value;
      }
      return next;
    });
  };

  const previewAmountCents = useMemo(() => {
    if (!paymentEnabled || !selectedPerson) return 0;
    if (!hasQuestions) {
      const prices = poll?.paymentConfig?.pricesByAgeType || {};
      const price = Number(prices[String(selectedPerson.ageType)] ?? 0);
      return Number.isFinite(price) && price > 0 ? price : 0;
    }
    let total = 0;
    (poll.questions || []).forEach((question, qIndex) => {
      if (question.type === 'text') return;
      const selectedIds = Array.isArray(answers[qIndex]) ? answers[qIndex] : [answers[qIndex]].filter(Boolean);
      (question.options || []).forEach((option) => {
        if (selectedIds.includes(option.id)) {
          total += optionPrice(option, selectedPerson.ageType);
        }
      });
    });
    return total;
  }, [answers, hasQuestions, paymentEnabled, poll, selectedPerson]);

  const buildAnswersPayload = () =>
    (poll.questions || []).map((question, qIndex) => ({
      questionId: question.id,
      response: Array.isArray(answers[qIndex]) ? answers[qIndex] : [answers[qIndex]].filter((v) => v != null && v !== ''),
    })).filter((a) => a.response.length > 0);

  const handleConfirm = async () => {
    if (!selectedPerson) return;
    setIsSubmitting(true);
    try {
      const { response, data } = await AdminRegisterUser(poll.id, {
        beneficiaryUserId: selectedPerson.id,
        answers: hasQuestions ? buildAnswersPayload() : [],
        paymentChoice: paymentEnabled ? paymentChoice : 'none',
      });

      if (!response.ok) {
        if (response.status === 403) {
          showNoti('No tienes permisos para inscribir a otras personas en esta encuesta.', true, 6000);
        } else if (response.status === 404) {
          showNoti(data?.Error || 'No se ha encontrado la encuesta o la persona.', true, 5000);
        } else if (response.status === 409) {
          showNoti(data?.Error || 'Esta inscripción ya está pagada y no puede sustituirse.', true, 6000);
        } else {
          showNoti(data?.Error || 'No se ha podido completar la inscripción.', true, 5000);
        }
        return;
      }

      const beneficiaryLabel = `${data?.beneficiary?.name || selectedPerson.name} ${data?.beneficiary?.surname || selectedPerson.surname}`.trim();

      // "Pagar ahora": el administrador paga inmediatamente, reutilizando
      // tal cual /Payments/Redsys/Create + /pago-redsys (mismo mecanismo
      // que ya usa un fallero pagando por su cuenta o por su familia).
      if (paymentEnabled && paymentChoice === 'online' && data?.beneficiary?.dni) {
        const returnMode = window?.Capacitor?.getPlatform?.() === 'android'
          ? 'android'
          : window?.Capacitor?.getPlatform?.() === 'ios'
            ? 'ios'
            : 'web';
        const payResult = await CreateRedsysPayment(poll.id, [data.beneficiary.dni], returnMode, 'card');
        if (payResult.response.ok && payResult.data?.redsys) {
          onSuccess?.();
          savePendingRedsysRedirect({ ...payResult.data.redsys, gatewayMethod: 'card' });
          try {
            localStorage.setItem('pending_redsys_payment', JSON.stringify({
              order: payResult.data.order,
              intentId: payResult.data.intentId,
              pollId: poll.id,
              receiptToken: payResult.data.receiptToken,
              startedAt: Date.now(),
            }));
          } catch { /* noop */ }
          window.location.assign('/pago-redsys');
          return;
        }
        showNoti(
          payResult.data?.Error || 'La inscripción se guardó, pero no se ha podido iniciar el pago online. Puedes intentarlo de nuevo desde el detalle.',
          true,
          7000
        );
        onSuccess?.();
        onClose?.();
        return;
      }

      showNoti(`${beneficiaryLabel} ha sido inscrito correctamente.`, false, 6000);
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('AdminRegisterUser error:', error);
      showNoti('No se ha podido completar la inscripción. Comprueba tu conexión e inténtalo de nuevo.', true, 6000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal admin-participant-overlay">
      <div className="modal-content admin-participant-modal">
        <div className="admin-participant-head">
          <button
            type="button"
            className="admin-participant-back"
            onClick={() => {
              if (step === 'search') { onClose?.(); return; }
              if (step === 'answer') { setStep('search'); return; }
              if (step === 'payment') { setStep(hasQuestions ? 'answer' : 'search'); return; }
              if (step === 'summary') { setStep(paymentEnabled ? 'payment' : (hasQuestions ? 'answer' : 'search')); return; }
            }}
            aria-label="Volver"
          >
            <span aria-hidden="true">‹</span>
          </button>
          <h3 className="admin-participant-title">
            {step === 'search' && 'Añadir participante'}
            {step === 'answer' && 'Responder encuesta'}
            {step === 'payment' && 'Forma de pago'}
            {step === 'summary' && 'Revisar inscripción'}
          </h3>
          <button type="button" className="admin-participant-close" onClick={onClose} aria-label="Cerrar">
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        <div className="admin-participant-body">
          {step === 'search' && (
            <>
              <label className="admin-participant-label" htmlFor="admin-search-input">Buscar fallero</label>
              <input
                id="admin-search-input"
                type="text"
                className="admin-participant-search-input"
                placeholder="Nombre, apellidos o DNI…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />

              {isSearching && <p className="admin-participant-hint">Buscando…</p>}
              {!isSearching && searchedOnce && results.length === 0 && (
                <p className="admin-participant-hint">Sin resultados.</p>
              )}

              <div className="admin-participant-results">
                {results.map((person) => (
                  <div className="admin-participant-result-card" key={person.id}>
                    <div className="admin-participant-result-info">
                      <span className="admin-participant-result-name">{person.name} {person.surname}</span>
                      <span className="admin-participant-result-meta">
                        {ageLabel(person.ageType)}{person.isRegistered ? ' · Ya inscrito/a' : ' · No inscrito/a'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="admin-participant-select-btn"
                      onClick={() => handleSelectPerson(person)}
                    >
                      {person.isRegistered ? 'Editar inscripción' : 'Añadir'}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {step === 'answer' && selectedPerson && (
            <>
              <p className="admin-participant-subject">
                Inscribir a<br /><strong>{selectedPerson.name} {selectedPerson.surname}</strong>
              </p>
              {(poll.questions || []).map((question, qIndex) => (
                <div key={question.id ?? qIndex} className="admin-participant-question">
                  <p className="admin-participant-question-text">{question.text}</p>
                  {question.type === 'text' ? (
                    <input
                      type="text"
                      className="admin-participant-text-input"
                      value={answers[qIndex] || ''}
                      onChange={(e) => handleAnswerChange(qIndex, question, e.target.value)}
                    />
                  ) : (
                    (question.options || []).map((option) => {
                      const checked = question.type === 'multiChoice'
                        ? Boolean(answers[qIndex]?.includes(option.id))
                        : answers[qIndex] === option.id;
                      const price = optionPrice(option, selectedPerson.ageType);
                      return (
                        <label key={option.id} className={`admin-participant-option${checked ? ' is-selected' : ''}`}>
                          <input
                            type={question.type === 'multiChoice' ? 'checkbox' : 'radio'}
                            checked={checked}
                            onChange={() => handleAnswerChange(qIndex, question, option.id)}
                          />
                          <span>
                            {optionText(option)}
                            {price > 0 ? ` (+${formatCents(price)})` : ''}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              ))}
              <button
                type="button"
                className="admin-participant-primary-btn"
                onClick={() => setStep(paymentEnabled ? 'payment' : 'summary')}
              >
                {paymentEnabled ? 'Continuar' : 'Revisar inscripción'}
              </button>
            </>
          )}

          {step === 'payment' && selectedPerson && (
            <>
              <p className="admin-participant-subject">
                Importe calculado: <strong>{formatCents(previewAmountCents)}</strong>
              </p>
              {previewAmountCents <= 0 ? (
                <p className="admin-participant-hint">Esta inscripción no requiere pago.</p>
              ) : (
                <div className="admin-participant-payment-options">
                  <button
                    type="button"
                    className={`admin-participant-payment-btn${paymentChoice === 'none' ? ' is-selected' : ''}`}
                    onClick={() => setPaymentChoice('none')}
                  >
                    Pendiente
                  </button>
                  <button
                    type="button"
                    className={`admin-participant-payment-btn${paymentChoice === 'manual' ? ' is-selected' : ''}`}
                    onClick={() => setPaymentChoice('manual')}
                  >
                    Pagará en casal
                  </button>
                  <button
                    type="button"
                    className={`admin-participant-payment-btn${paymentChoice === 'online' ? ' is-selected' : ''}`}
                    onClick={() => setPaymentChoice('online')}
                  >
                    Pagar ahora
                  </button>
                </div>
              )}
              <button type="button" className="admin-participant-primary-btn" onClick={() => setStep('summary')}>
                Revisar inscripción
              </button>
            </>
          )}

          {step === 'summary' && selectedPerson && (
            <>
              <div className="admin-participant-summary">
                <p className="admin-participant-summary-name">{selectedPerson.name} {selectedPerson.surname}</p>
                {hasQuestions && buildAnswersPayload().map((answer) => {
                  const question = (poll.questions || []).find((q) => String(q.id) === String(answer.questionId));
                  const labels = (answer.response || [])
                    .map((value) => {
                      if (question?.type === 'text') return value;
                      const opt = (question?.options || []).find((o) => String(o.id) === String(value));
                      return opt ? optionText(opt) : value;
                    })
                    .join(', ');
                  return (
                    <p key={answer.questionId} className="admin-participant-summary-row">
                      {question?.text}: <strong>{labels}</strong>
                    </p>
                  );
                })}
                {paymentEnabled && (
                  <p className="admin-participant-summary-row">
                    Importe: <strong>{formatCents(previewAmountCents)}</strong>
                  </p>
                )}
                {paymentEnabled && previewAmountCents > 0 && (
                  <p className="admin-participant-summary-row">
                    Forma de pago: <strong>
                      {paymentChoice === 'manual' ? 'Pagará en casal' : paymentChoice === 'online' ? 'Pagar ahora (tú como pagador)' : 'Pendiente de decidir'}
                    </strong>
                  </p>
                )}
              </div>
              <button
                type="button"
                className="admin-participant-primary-btn"
                onClick={handleConfirm}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Guardando…' : 'Confirmar inscripción'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAddParticipantModal;
